import { db } from '../firebase/config';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  serverTimestamp,
  orderBy,
} from 'firebase/firestore';

class VerificationService {
  // Get verifications with optional status filter
  async getVerifications(status = 'pending') {
    try {
      let verificationQuery;
      
      if (status === 'all') {
        verificationQuery = query(
          collection(db, 'identity_verifications'),
          orderBy('submittedAt', 'desc')
        );
      } else {
        verificationQuery = query(
          collection(db, 'identity_verifications'),
          where('status', '==', status),
          orderBy('submittedAt', 'desc')
        );
      }
      
      const snapshot = await getDocs(verificationQuery);
      const verifications = [];
      
      for (const docSnapshot of snapshot.docs) {
        const verificationData = docSnapshot.data();
        
        // Get user data
        const userDoc = await getDoc(doc(db, 'users', verificationData.userId));
        const userData = userDoc.exists() ? userDoc.data() : null;
        
        verifications.push({
          id: docSnapshot.id,
          ...verificationData,
          user: {
            id: verificationData.userId,
            displayName: userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : 'Utilisateur inconnu',
            email: userData ? userData.email : '',
            phone: userData ? userData.phone : '',
            firstName: userData ? userData.firstName : '',
            lastName: userData ? userData.lastName : '',
            birthDate: userData ? userData.birthDate : ''
          }
        });
      }
      
      return verifications;
    } catch (error) {
      console.error('Error getting verifications:', error);
      throw error;
    }
  }
  
  // Get a single verification by ID
  async getVerification(id) {
    try {
      const verificationDoc = await getDoc(doc(db, 'identity_verifications', id));
      
      if (!verificationDoc.exists()) {
        throw new Error('Verification not found');
      }
      
      const verificationData = verificationDoc.data();
      
      // Get user data
      const userDoc = await getDoc(doc(db, 'users', verificationData.userId));
      const userData = userDoc.exists() ? userDoc.data() : null;
      
      return {
        id: verificationDoc.id,
        ...verificationData,
        user: {
          id: verificationData.userId,
          displayName: userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : 'Utilisateur inconnu',
          email: userData ? userData.email : '',
          phone: userData ? userData.phone : '',
          firstName: userData ? userData.firstName : '',
          lastName: userData ? userData.lastName : '',
          birthDate: userData ? userData.birthDate : ''
        }
      };
    } catch (error) {
      console.error('Error getting verification:', error);
      throw error;
    }
  }
  
  // Approve a verification
  async approveVerification(id, adminUser) {
    try {
      const verificationRef = doc(db, 'identity_verifications', id);
      const verificationDoc = await getDoc(verificationRef);
      
      if (!verificationDoc.exists()) {
        throw new Error('Verification not found');
      }
      
      const verificationData = verificationDoc.data();
      
      // Update verification status
      await updateDoc(verificationRef, {
        status: 'approved',
        processedAt: serverTimestamp(),
        processedBy: adminUser ? adminUser.displayName : 'Admin',
        history: [
          ...(verificationData.history || []),
          {
            action: 'Approbation',
            date: serverTimestamp(),
            by: adminUser ? adminUser.displayName : 'Admin'
          }
        ]
      });
      
      // Update user's verification status
      const userRef = doc(db, 'users', verificationData.userId);
      await updateDoc(userRef, {
        isVerified: true,
        verifiedAt: serverTimestamp(),
        verificationId: id
      });
      
      return true;
    } catch (error) {
      console.error('Error approving verification:', error);
      throw error;
    }
  }
  
  // Reject a verification
  async rejectVerification(id, reason, adminUser) {
    try {
      const verificationRef = doc(db, 'identity_verifications', id);
      const verificationDoc = await getDoc(verificationRef);
      
      if (!verificationDoc.exists()) {
        throw new Error('Verification not found');
      }
      
      const verificationData = verificationDoc.data();
      
      // Update verification status
      await updateDoc(verificationRef, {
        status: 'rejected',
        processedAt: serverTimestamp(),
        processedBy: adminUser ? adminUser.displayName : 'Admin',
        rejectReason: reason,
        history: [
          ...(verificationData.history || []),
          {
            action: 'Rejet',
            date: serverTimestamp(),
            by: adminUser ? adminUser.displayName : 'Admin',
            note: reason
          }
        ]
      });
      
      // Update user's verification status
      const userRef = doc(db, 'users', verificationData.userId);
      await updateDoc(userRef, {
        verificationRejected: true,
        verificationRejectedAt: serverTimestamp(),
        verificationRejectedReason: reason
      });
      
      return true;
    } catch (error) {
      console.error('Error rejecting verification:', error);
      throw error;
    }
  }
  
  // Get verification statistics
  async getVerificationStats() {
    try {
      const pendingQuery = query(
        collection(db, 'identity_verifications'),
        where('status', '==', 'pending')
      );
      
      const approvedQuery = query(
        collection(db, 'identity_verifications'),
        where('status', '==', 'approved')
      );
      
      const rejectedQuery = query(
        collection(db, 'identity_verifications'),
        where('status', '==', 'rejected')
      );
      
      const [pendingSnapshot, approvedSnapshot, rejectedSnapshot] = await Promise.all([
        getDocs(pendingQuery),
        getDocs(approvedQuery),
        getDocs(rejectedQuery)
      ]);
      
      return {
        pending: pendingSnapshot.size,
        approved: approvedSnapshot.size,
        rejected: rejectedSnapshot.size,
        total: pendingSnapshot.size + approvedSnapshot.size + rejectedSnapshot.size
      };
    } catch (error) {
      console.error('Error getting verification stats:', error);
      throw error;
    }
  }
}

export const verificationService = new VerificationService();
